import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState} from "react";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {updateMenteeWithMentor} from "@/services/menteeService.ts";
import {Card, CardDescription, CardHeader} from "@/components/ui/card.tsx";
import {SectionTitle} from "@/components/ui/headings";
import {toast} from "@/components/ui/use-toast.ts";
import {MenteeWithPreferenceAndSuggestionsDetail, UpdateMenteeWithMentorIdDto} from "@/types/Mentee.ts";
import {useTranslation} from "react-i18next";
import SuggestedMentorCard from "@/components/SuggestedMentorCard.tsx";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";

type PendingSelection = { mentorId: number; mentorName: string };

type SuggestionsProps = {
    menteeWithSuggestedMentors: MenteeWithPreferenceAndSuggestionsDetail,
    maxNumberOfMentees?: number,
    isAdmin?: boolean,
    closeDialog?: () => void,
    selectMentorDeadline?: Date,
}

export const Suggestions = ({
                                menteeWithSuggestedMentors,
                                maxNumberOfMentees = 8,
                                isAdmin = false,
                                closeDialog,
                                selectMentorDeadline,
                            }: SuggestionsProps) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);

    const suggestionsList = menteeWithSuggestedMentors?.menteesSuggestedMentors ?? [];

    // Separate suggestions into available and unavailable
    const availableMentors = suggestionsList.filter(
        suggestion => maxNumberOfMentees - (suggestion.mentor?.mentees?.length || 0) > 0
    );
    const unavailableMentors = suggestionsList.filter(
        suggestion => maxNumberOfMentees - (suggestion.mentor?.mentees?.length || 0) === 0
    );

    const affinitySuggestions = availableMentors.filter(
        suggestion => suggestion.affinityGroupSuggestion === true
    );
    const otherSuggestions = availableMentors.filter(
        suggestion => suggestion.affinityGroupSuggestion !== true
    );

    const handleSelect = (mentorId: number) => {
        const suggestion = suggestionsList.find(s => s.mentor?.id === mentorId);
        const mentorName = suggestion?.mentor?.user?.fullName
            ?? ([suggestion?.mentor?.user?.firstName, suggestion?.mentor?.user?.lastName].filter(Boolean).join(" ") || "");
        setPendingSelection({ mentorId, mentorName });
    };

    const handleConfirmSelection = () => {
        if (!pendingSelection || !menteeWithSuggestedMentors?.id) return;
        updateMutation.mutate({
            mentorId: pendingSelection.mentorId,
            id: menteeWithSuggestedMentors.id,
        });
        setPendingSelection(null);
    };

    const handleCancelSelection = () => {
        setPendingSelection(null);
    };

    const onUpdateSuccess = async () => {
        await queryClient.invalidateQueries({queryKey: queryKeys.fetchMentees.one});
        toast({
            variant: "success",
            title: t("common.successTitle"),
            description: t("dashboard.mentorAssignSuccess"),
        });
    };

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: UpdateMenteeWithMentorIdDto & { id: number }) => updateMenteeWithMentor(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
        onSettled: () => {
            setPendingSelection(null);
            closeDialog?.();
        },
    });

    const deadlineStr = selectMentorDeadline?.toDateString() ?? "—";

    return (
        <>
            <AlertDialog open={!!pendingSelection} onOpenChange={(open) => !open && setPendingSelection(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("dashboard.confirmMentorSelectionTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("dashboard.confirmMentorSelectionDescription", {
                                name: pendingSelection?.mentorName ?? "",
                                deadline: deadlineStr,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelSelection} disabled={updateMutation.isPending}>
                            {t("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmSelection} disabled={updateMutation.isPending}>
                            {t("common.confirm")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {affinitySuggestions.length > 0 && (
                <>
                    <SectionTitle className="p-2">{t("affinity.groupSuggestionsTitle")}</SectionTitle>
                    <SuggestedMentorCard
                        menteeWithSuggestedMentors={menteeWithSuggestedMentors}
                        suggestions={affinitySuggestions}
                        available={true}
                        isAdmin={isAdmin}
                        handleSelect={handleSelect}
                        startIndex={0}
                    />
                </>
            )}

            {otherSuggestions.length > 0 && (
                <>
                    <SectionTitle className="p-2 pt-6">
                        {affinitySuggestions.length > 0 ? t("affinity.otherSectionTitle") : t("suggestions.suggestedMentors")}
                    </SectionTitle>
                    <SuggestedMentorCard
                        menteeWithSuggestedMentors={menteeWithSuggestedMentors}
                        suggestions={otherSuggestions}
                        available={true}
                        isAdmin={isAdmin}
                        handleSelect={handleSelect}
                        startIndex={affinitySuggestions.length}
                    />
                </>
            )}

            {unavailableMentors && unavailableMentors.length > 0 &&
                <>
                    <SectionTitle className="p-2 pt-6">{t("dashboard.mentorshipGroupFull")}</SectionTitle>
                    <SuggestedMentorCard
                        menteeWithSuggestedMentors={menteeWithSuggestedMentors}
                        suggestions={unavailableMentors}
                        available={false}
                        isAdmin={isAdmin}
                        handleSelect={handleSelect}
                    />
                </>
            }

            {(!availableMentors || availableMentors.length === 0) && (
                <Card className="mt-6">
                    <CardHeader>
                        <CardDescription className="text-base">
                            {t("dashboard.noSuggestedMentors")}
                        </CardDescription>
                    </CardHeader>
                </Card>
            )}
        </>
    )
}
